import { Link } from "react-router";
import { ROUTES } from "../links";
import { usePageTitle } from "./usePageTitle";
import { PageFaq } from "../widgets/PageFaq";

const CAPS: [string, string][] = [
  [
    "Cash flow",
    "Twelve months of real spending, sorted and named, with recurring charges pulled out.",
  ],
  ["Net worth", "Where you are, where you were when you joined, and where both paths land."],
  ["Debt", "What each payment buys, when every debt clears, whether consolidating is worth it."],
  [
    "Retirement",
    "The nest egg giving you a 95% chance of never running out, and the date you hit it.",
  ],
  ["Tax", "Which account each dollar belongs in, and the order to draw them down."],
  ["Education", "What four years cost, what the grant is worth, whether the fund covers it."],
  ["Priorities", "Score what matters and watch the plan reorder itself around you."],
  ["Decisions", "A house, a car, a job change, three months off. Run it before you do it."],
];

const CLAIMS: [string, string][] = [
  [
    "Projections grow at the expected rate, not a random one",
    "Every line you see grows at exactly 6% a year. Real markets don&rsquo;t. This shows you the middle of the range rather than the range, and we say so wherever a projection appears.",
  ],
  [
    "The retirement target is the one place variation is modelled",
    "Your nest egg isn&rsquo;t twenty-five times your spending. It&rsquo;s the amount that gives you a 95% chance of the money outlasting you, worked out from how long people actually live and how unevenly markets actually behave.",
  ],
  [
    "It tells you when the plan fails",
    "If there&rsquo;s a month ahead where the money on hand doesn&rsquo;t cover what&rsquo;s due, Abi names the date, the amount and what closes it. That warning is the most valuable thing a plan can give you and it is not buried.",
  ],
  [
    "Some numbers are assumptions until you correct them",
    "Contribution room, workplace plan details, years in Canada. Abi fills the gaps with documented defaults and shows you which ones are guesses, ranked by how much the answer would move.",
  ],
];

/** What Abi does (the prototype's `data-s="abi"`). */
export function WhatAbiDoesPage() {
  usePageTitle("What Abi does");
  return (
    <div className="spage on" data-s="abi">
      <header className="hero" style={{ paddingBottom: 0 }}>
        <div className="wrap">
          <h1>
            Abi searches thousands of versions of your life and keeps the <em>best</em> one.
          </h1>
          <p className="lede">
            Not a calculator answering one question. A planner searching thousands of versions of
            your life and keeping the best one.
          </p>
        </div>
      </header>
      <section className="band">
        <div className="wrap">
          <div className="cards three">
            <div className="card">
              <p className="mono">The goal</p>
              <h3>Your priorities, weighted</h3>
              <p>
                Abi doesn&rsquo;t maximise net worth and it doesn&rsquo;t rank products. It
                maximises how completely the things you said mattered actually get funded, weighted
                by the score you gave each one. Overfunding a goal earns nothing, so surplus moves
                to whatever still needs it.
              </p>
            </div>
            <div className="card">
              <p className="mono">The board</p>
              <h3>A day-by-day simulation</h3>
              <p>
                Paycheques land with the right deductions. Debts accrue interest on their own
                schedules. Tax gets filed every year and the refund shows up in spring. RRSPs
                convert to RRIFs at 71. All of it, every day, to age 90.
              </p>
            </div>
            <div className="card">
              <p className="mono">The moves</p>
              <h3>Things you could actually do</h3>
              <p>
                Pay a bill, pay down a debt, move money into an account, draw money out, consolidate
                debt into the mortgage, open an account you don&rsquo;t have. Every recommendation
                is built from moves a real person can make on a Tuesday.
              </p>
            </div>
          </div>
          <p className="railhint">Swipe</p>
          <div className="shead" style={{ marginTop: 76 }}>
            <p className="mono">What it covers</p>
            <h2>Every part of the picture, in one model.</h2>
            <p>
              These aren&rsquo;t separate tools stitched together. They&rsquo;re all the same
              simulation, viewed from different angles, which is why changing one of them changes
              the rest.
            </p>
          </div>
          <div className="cards four" id="abi-caps">
            {CAPS.map(([k, d]) => (
              <div className="card lift" key={k}>
                <h3 style={{ fontSize: 20 }}>{k}</h3>
                <p>{d}</p>
              </div>
            ))}
          </div>
          <div className="figure" style={{ marginTop: 40 }}>
            <div className="fhead">
              <h3>What the engine does and doesn&rsquo;t claim</h3>
            </div>
            {CLAIMS.map(([b, p]) => (
              <div className="secrow" key={b}>
                <span className="ic">&#8226;</span>
                <div>
                  <b>{b}</b>
                  <p dangerouslySetInnerHTML={{ __html: p }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <PageFaq page="abi" />
      <EndCta />
    </div>
  );
}

export function EndCta() {
  return (
    <section className="endcta">
      <div className="wrap">
        <h2>Point it at your actual numbers.</h2>
        <p>The engine is only as good as what it can see. Ten minutes of setup is the whole ask.</p>
        <div className="cta">
          <Link className="sbtn p big" to={ROUTES.start}>
            Start your plan
          </Link>
        </div>
      </div>
    </section>
  );
}
